import DatePickerModalM from "@common/components/modal/DatePickerModalM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { weekDaysObject } from "@data/constants/activitiesConstants"
import moment from "moment"
import "moment/locale/fr"
import React, { useMemo, useState } from "react"
import { TouchableOpacity, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import {
    chipLabelOf,
    liveTimesLabel,
    slotDayOf,
    slotKeyOf,
    slotRangeLabel,
    startingDatePickerValue,
    timeRangeLabel,
} from "./maDispoSlot"

/** The single horizontal gutter of the screen. One value, so nothing drifts. */
export const GUTTER = 16

const WEEKDAY_ORDER = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
]

/**
 * The days the week renders, canonically ordered (Mon → Sun): every day of the
 * régime grid, PLUS any day already carrying a créneau without being in it.
 *
 * That second set is not hypothetical. The grid is the `essential` (cours
 * partagés) one, Monday→Saturday — but an ENGAGED créneau is derived from the
 * tutor's live sessions, which include `individual`, whose own grid runs 7 days
 * a week. A Sunday cours particulier would otherwise vanish from the screen: the
 * tutor would never see the créneau is locked, and the day count would disagree
 * with the BO.
 */
const orderedWeekDays = (grid, slots) => {
    const daysWithSlots = new Set(slots.map((s) => slotDayOf(s.slot)))

    return WEEKDAY_ORDER.filter(
        (day) => (grid?.[day]?.length ?? 0) > 0 || daysWithSlots.has(day)
    )
}

/** "Monday 09:00" → "09:00" — the time half of a slot key. */
const timeOfKey = (flatSlot) => slotKeyOf(flatSlot).split(" ")[1]

/**
 * Every hour ONE day can show, in clock order — the régime grid merged with what
 * the tutor already carries.
 *
 * Merged rather than concatenated so a declared hour and its grid twin are ONE
 * chip: the semaine-type still holds legacy non-padded starts ("Saturday 9")
 * whose padded twin is in the grid, and rendering both would offer the same hour
 * twice. A declared créneau outside the grid keeps its place — it is real, and
 * the tutor must be able to see and drop it.
 *
 * @returns {Array<{time: string, label: string, slot: ?object, engaged: boolean,
 *                   liveTimes: string[]}>}
 */
const hoursOfDay = (day, grid, slots) => {
    const byTime = new Map()

    for (const time of grid?.[day] ?? []) {
        byTime.set(time, {
            time,
            label: timeRangeLabel(time),
            slot: null,
            engaged: false,
            liveTimes: [],
        })
    }
    for (const slot of slots) {
        if (slotDayOf(slot.slot) !== day) continue
        const time = timeOfKey(slot.slot)
        byTime.set(time, {
            time,
            // The stored créneau's OWN label: a range keeps its end rather than
            // borrowing the grid's hourly step.
            label: slotRangeLabel(slot.slot),
            slot,
            engaged: !!slot.engaged,
            freed: !!slot.freed,
            // The live's real start(s) inside this hour. The backend folds a live
            // onto the grid hour that contains it, because that is the cell this
            // grid has — but the tutor knows the live by the time it starts, so
            // the chip says that and never renames it. Empty on an open créneau.
            liveTimes: slot.live_times ?? [],
        })
    }

    // Zero-padded on both sides, so a lexicographic sort is a clock sort.
    return [...byTime.values()].sort((a, b) => a.time.localeCompare(b.time))
}

/**
 * The « Ma dispo » editor — the two numbered steps (① starting date, ② the week),
 * with no screen chrome.
 *
 * Extracted so the tutor meets ONE availability experience wherever they set it:
 * the standalone screen wraps this in its banner + sticky CTA and saves through
 * the confirm endpoint, while the profile stepper embeds it as a step and lets
 * the form submit carry the values. It owns only presentation state (the date
 * picker); every change is handed back to the caller, which decides what a change
 * means — a staged draft here, a form field there.
 *
 * Step ① carries no warning: a validation whose proposal floor is absent or behind
 * us now writes today rather than being refused, so the only thing left to say about
 * a stale date is the date itself, and the picker opens where the tutor can act
 * ({@see startingDatePickerValue}).
 *
 * The week is a GRID OF HOURS, not a list of cards behind a modal. Each day shows
 * every hour it can offer and the tutor taps to take or drop one, so posing five
 * créneaux is five taps in place instead of five modal round-trips — and the
 * state of the whole week is legible without opening anything.
 */
export const MaDispoEditor = ({
    slots,
    grid,
    startingDate,
    onPickStartingDate,
    onAddSlot,
    onRemoveSlot,
    onPressEngaged,
}) => {
    const [datePickerVisible, setDatePickerVisible] = useState(false)

    const startingLabel = startingDate
        ? moment(startingDate).format("dddd D MMMM YYYY")
        : "Choisir une date"

    return (
        <>
            <StepLabel n={1} text="Je suis disponible à partir de…" />
            <TouchableOpacity
                onPress={() => setDatePickerVisible(true)}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: GUTTER,
                    paddingVertical: 10,
                }}
            >
                <FontAwesome5
                    name="calendar-alt"
                    size={15}
                    color={theme.colors.light}
                    style={{ marginRight: 10 }}
                />
                <TextM fontWeight="Bold" fontSize={14}>
                    {startingLabel}
                </TextM>
                <TextM
                    fontWeight="Bold"
                    fontSize={13}
                    color={theme.colors.link}
                    style={{ marginLeft: 10 }}
                >
                    modifier
                </TextM>
            </TouchableOpacity>

            <StepLabel
                n={2}
                text="Je choisis mes créneaux"
                hint="Touche une heure pour la proposer, touche-la à nouveau pour la retirer."
            />
            <MaDispoWeekGrid
                slots={slots}
                grid={grid}
                onAddSlot={onAddSlot}
                onRemoveSlot={onRemoveSlot}
                onPressEngaged={onPressEngaged}
            />

            <WeekSummary slots={slots} />

            <DatePickerModalM
                visible={datePickerVisible}
                onDismiss={() => setDatePickerVisible(false)}
                // NOT the saved date when that one is past: the calendar would
                // open on a month where `validRange` disables every day.
                // {@see startingDatePickerValue}
                value={startingDatePickerValue(startingDate)}
                onConfirm={(dateStr) => {
                    setDatePickerVisible(false)
                    onPickStartingDate(dateStr)
                }}
                saveLabel="Continuer"
                // The change warning belongs to a date the tutor VALIDATED, not to
                // every day they tap while browsing the calendar.
                applyOnSaveOnly
                // Mirror the server bounds ([today, today + 1 year]) so past /
                // far-future dates can't be picked and bounce back on a 422.
                validRange={{
                    startDate: moment().startOf("day").toDate(),
                    endDate: moment().add(1, "year").toDate(),
                }}
            />
        </>
    )
}

/**
 * The week alone — one row of hour chips per day, no step labels nor summary.
 * « Libérer des créneaux » confirms the week through this same grid, so the tutor
 * edits it with the gestures they already know from « Ma dispo ». A slot flagged
 * `freed` renders struck through and inert: it is being given up, not edited.
 */
export const MaDispoWeekGrid = ({
    slots,
    grid,
    onAddSlot,
    onRemoveSlot,
    onPressEngaged,
    onPressFreed,
}) => {
    const weekDays = useMemo(() => orderedWeekDays(grid, slots), [grid, slots])
    const days = useMemo(
        () =>
            weekDays.map((day) => ({
                day,
                hours: hoursOfDay(day, grid, slots),
            })),
        [weekDays, grid, slots]
    )

    return days.map(({ day, hours }) => (
        <DayRow
            key={day}
            day={day}
            hours={hours}
            onAdd={onAddSlot}
            onRemove={onRemoveSlot}
            onPressEngaged={onPressEngaged}
            onPressFreed={onPressFreed}
        />
    ))
}

// ── Sub-components ──────────────────────────────────────────────────────────

const StepLabel = ({ n, text, hint }) => (
    <View style={{ paddingHorizontal: GUTTER, marginTop: 28 }}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View
                style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    backgroundColor: theme.colors.primary,
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 10,
                }}
            >
                <TextM
                    fontWeight="Bold"
                    fontSize={12}
                    color={theme.colors.white}
                >
                    {n}
                </TextM>
            </View>
            <TextM fontWeight="Bold" fontSize={15} wrap style={{ flex: 1 }}>
                {text}
            </TextM>
        </View>
        {!!hint && (
            <TextM
                fontSize={13}
                color={theme.colors.light}
                wrap
                style={{ marginTop: 6, marginLeft: 32 }}
            >
                {hint}
            </TextM>
        )}
    </View>
)

const DayRow = ({ day, hours, onAdd, onRemove, onPressEngaged, onPressFreed }) => {
    const engaged = hours.filter((h) => h.engaged && !h.freed).length
    const freed = hours.filter((h) => h.freed).length
    const taken = hours.filter((h) => h.slot && !h.engaged).length
    // Out of the hours the tutor can actually take — an engaged one is not on
    // offer, so counting it in the denominator promised a choice that is not there.
    const offerable = hours.length - engaged - freed
    // The day's own hours, so a chip never renames itself to a time one of its
    // neighbours already carries ({@see chipLabelOf}).
    const dayTimes = hours.map((h) => h.time)

    return (
        <View style={{ paddingHorizontal: GUTTER, marginTop: 20 }}>
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "baseline",
                    marginBottom: 10,
                }}
            >
                <TextM fontWeight="Bold" fontSize={14}>
                    {weekDaysObject[day]}
                </TextM>
                {/* Same read-out as the family grid: how many of the day's hours are
                taken, so the tutor scans the week without counting chips. */}
                <TextM
                    fontSize={12}
                    color={theme.colors.light}
                    style={{ marginLeft: 8 }}
                >
                    {offerable > 0
                        ? `${taken}/${offerable} dispo${taken > 1 ? "s" : ""}`
                        : ""}
                    {engaged
                        ? `${offerable > 0 ? " · " : ""}${engaged} live${engaged > 1 ? "s" : ""}`
                        : ""}
                </TextM>
            </View>
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                {hours.map((hour) => (
                    <HourChip
                        key={hour.time}
                        day={day}
                        hour={hour}
                        dayTimes={dayTimes}
                        onAdd={onAdd}
                        onRemove={onRemove}
                        onPressEngaged={onPressEngaged}
                        onPressFreed={onPressFreed}
                    />
                ))}
            </View>
        </View>
    )
}

/**
 * What the week adds up to, at its foot — the read the tutor came for once the
 * créneau-by-créneau work is done: how much of their week is already placed, how
 * much they are offering on top, and what that commits them to in total.
 *
 * Counted off the DRAFT, so it moves under the tutor's finger as they take and
 * drop hours — the same list the grid above renders.
 */
const WeekSummary = ({ slots }) => {
    const live = slots.filter((s) => s.engaged).length
    const open = slots.length - live

    // Sans aucun live, « 0 avec un live » et un total égal aux dispos ne
    // racontent rien : deux cases sur trois répètent la troisième ou affichent
    // zéro. Le tuteur concerné ne lit qu'un chiffre — celui qu'il vient de poser.
    const tiles = [
        ...(live > 0
            ? [
                  {
                      key: "live",
                      value: live,
                      label: `heure${live > 1 ? "s" : ""} avec un live`,
                      color: theme.colors.primary,
                  },
              ]
            : []),
        {
            key: "open",
            value: open,
            label: `heure${open > 1 ? "s" : ""} dispo${open > 1 ? "s" : ""}`,
            color: theme.colors.success,
        },
        ...(live > 0
            ? [
                  {
                      key: "total",
                      value: live + open,
                      label: "au total par semaine",
                      color: theme.colors.grey,
                  },
              ]
            : []),
    ]

    return (
        <View
            style={{
                marginHorizontal: GUTTER,
                marginTop: 28,
                flexDirection: "row",
                backgroundColor: theme.colors.whiteAlmost,
                borderWidth: 1,
                borderColor: theme.colors.lightMore,
                borderRadius: 14,
                paddingVertical: 16,
            }}
        >
            {tiles.map((tile, i) => (
                <React.Fragment key={tile.key}>
                    {i > 0 && (
                        <View
                            style={{
                                width: 1,
                                backgroundColor: theme.colors.lightMore,
                                marginVertical: 2,
                            }}
                        />
                    )}
                    <SummaryTile
                        value={tile.value}
                        label={tile.label}
                        color={tile.color}
                    />
                </React.Fragment>
            ))}
        </View>
    )
}

const SummaryTile = ({ value, label, color }) => (
    <View style={{ flex: 1, alignItems: "center", paddingHorizontal: 6 }}>
        <TextM fontWeight="Bold" fontSize={22} color={color}>
            {value}
        </TextM>
        <TextM
            fontSize={11.5}
            color={theme.colors.light}
            wrap
            style={{ textAlign: "center", marginTop: 3, lineHeight: 15 }}
        >
            {label}
        </TextM>
    </View>
)

/**
 * One hour, in one of three states — and the ONLY control of the week.
 *
 * The colour says what the créneau IS, never how fresh the week is: freshness is
 * a single stamp on the whole semaine ({@see
 * \App\Services\Tutor\TutorAvailability::freshness}), so painting every posed
 * hour with it announced a per-créneau problem the tutor cannot act on
 * per-créneau. Only the banner carries freshness.
 */
const HourChip = ({ day, hour, dayTimes, onAdd, onRemove, onPressEngaged, onPressFreed }) => {
    const { engaged, freed, slot, label, time, liveTimes } = hour
    const taken = !!slot
    const chipLabel = chipLabelOf(hour, dayTimes)

    const onPress = () => {
        // A créneau freed by an earlier gesture is inert; one only marked in this draft unmarks.
        if (freed) return onPressFreed?.(slot)
        if (engaged) return onPressEngaged?.(slot)
        // A draft edit, reversible by the same tap until « Confirmer ma dispo » —
        // so it asks nothing. A confirmation dialog here guarded a change that
        // undoes itself.
        return taken ? onRemove?.(slot) : onAdd?.(day, time)
    }

    // Deliberately the SAME vocabulary as the family grid
    // ({@see \common\student-diagnostic-stepper\ConstraintsGridStep}): a chosen
    // hour is GREEN with a solid border and a check, an available one is dashed
    // and muted. Families and tutors pick their créneaux out of the same object,
    // so it should not look like two products. The locked state is the tutor's
    // own — a family has no live to protect — and wears PRIMARY, the same colour
    // the week summary counts those hours in: one thing, one colour, wherever the
    // tutor meets it on this screen.
    const tone = freed
        ? {
              bg: theme.colors.whiteAlmost,
              border: theme.colors.lightMore,
              borderStyle: "solid",
              text: theme.colors.light,
              icon: "times",
          }
        : engaged
        ? {
              bg: theme.colors.primaryLighter,
              border: theme.colors.primary,
              borderStyle: "solid",
              text: theme.colors.primary,
              icon: "video",
          }
        : taken
          ? {
                bg: theme.colors.successLight,
                border: theme.colors.success,
                borderStyle: "solid",
                text: theme.colors.success,
                icon: "check",
            }
          : {
                bg: theme.colors.whiteAlmost,
                border: theme.colors.light,
                borderStyle: "dashed",
                text: theme.colors.light,
                icon: null,
            }

    return (
        <TouchableOpacity
            activeOpacity={0.7}
            onPress={onPress}
            // The one inert state: a créneau already freed by an earlier gesture.
            disabled={freed && !slot?.releasable}
            accessibilityRole="button"
            // No `disabled` here: on native, TouchableOpacity reads it as the prop and drops
            // the tap, while an engaged chip opens its warning or frees the créneau.
            accessibilityState={{ selected: taken }}
            // The a11y label keeps BOTH: the hour of the week the chip occupies,
            // and the live inside it. The visible label has room for only one.
            accessibilityLabel={`${weekDaysObject[day]} ${label}${
                freed
                    ? " — libéré"
                    : engaged
                    ? ` — occupé par un live${liveTimesLabel(liveTimes)}`
                    : taken
                      ? " — dispo"
                      : ""
            }`}
            style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: tone.bg,
                borderWidth: 1,
                borderColor: tone.border,
                borderStyle: tone.borderStyle,
                borderRadius: 10,
                paddingVertical: 10,
                paddingHorizontal: 12,
                marginRight: 8,
                marginBottom: 8,
            }}
        >
            {/* Reserved even when empty, so a chip does not resize on tap and the
                row does not reflow under the tutor's finger. */}
            <View style={{ width: 16, marginRight: 6 }}>
                {!!tone.icon && (
                    <FontAwesome5
                        name={tone.icon}
                        size={12}
                        color={tone.text}
                    />
                )}
            </View>
            <TextM
                fontWeight="Bold"
                fontSize={13}
                color={tone.text}
                style={freed ? { textDecorationLine: "line-through" } : null}
            >
                {chipLabel}
            </TextM>
        </TouchableOpacity>
    )
}
